import { useState } from "react";
import { Link } from "react-router-dom";
import { creators } from "@/mocks/creators";

const tabs = [
  { key: "recent", label: "最新上榜" },
  { key: "contrast", label: "反差最高" },
  { key: "heat", label: "讨论最多" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

export default function RankingBoard() {
  const [active, setActive] = useState<TabKey>("recent");

  const sorted = [...creators].sort((a, b) => {
    if (active === "contrast") return b.contrastScore - a.contrastScore;
    if (active === "heat") return b.heat - a.heat;
    return b.afterYear - a.afterYear || b.id - a.id;
  });

  const list = sorted.slice(0, 6);

  return (
    <section className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs tracking-[0.24em] text-primary-600">RANKING</p>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              对比榜单速览
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              切换下面标签可以换一种排法：想看谁刚上榜、谁的落差最大、谁被讨论得最多，都在这里。
            </p>
          </div>

          <div
            role="tablist"
            aria-label="榜单排序方式"
            className="inline-flex items-center gap-1 self-start rounded-full border border-background-300 bg-background-100 p-1"
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                type="button"
                aria-selected={active === tab.key}
                onClick={() => setActive(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === tab.key
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <ol className="mt-8 flex flex-col divide-y divide-background-200 border-y border-background-200">
          {list.map((creator, index) => (
            <li key={creator.slug}>
              <Link
                to={`/detail/${creator.slug}`}
                className="group flex cursor-pointer flex-col gap-4 py-5 sm:flex-row sm:items-center"
              >
                <span className="font-heading text-2xl font-black text-background-400 sm:w-12">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="flex w-full items-center gap-4 sm:w-auto">
                  <div className="flex h-16 w-28 shrink-0 overflow-hidden rounded-md border border-background-200">
                    <img
                      src={creator.beforeImage}
                      alt={`${creator.name} 早期形象缩略图`}
                      title={`${creator.name} 早期形象对比`}
                      className="h-full w-1/2 object-cover object-top"
                    />
                    <img
                      src={creator.afterImage}
                      alt={`${creator.name} 近期形象缩略图`}
                      title={`${creator.name} 近期形象对比`}
                      className="h-full w-1/2 object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="flex flex-wrap items-center gap-2 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600">
                      {creator.name}
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-normal text-secondary-900">
                        {creator.platform}
                      </span>
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-500 sm:text-sm">
                      {creator.summary}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 sm:ml-auto sm:shrink-0">
                  <span className="flex flex-col">
                    <span className="text-[11px] text-foreground-500">反差指数</span>
                    <span className="font-heading text-base font-bold text-primary-600">
                      {creator.contrastScore}
                    </span>
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[11px] text-foreground-500">平台</span>
                    <span className="text-sm text-foreground-800">{creator.beforeYear}-{creator.afterYear}</span>
                  </span>
                  <i className="ri-arrow-right-line text-lg text-foreground-400 transition-colors group-hover:text-primary-600"></i>
                </div>
              </Link>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex justify-center">
          <Link
            to="/bang"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看完整榜单（{creators.length} 组精选）
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}